import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { tr } from '@/i18n/tr';
import styles from './bottomSheet.module.scss';

export interface BottomSheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Sticky action row rendered below the scrollable body (e.g. Temizle / Uygula). */
  footer?: ReactNode;
}

/**
 * Modal sheet: slides from the bottom on mobile (mockup pattern), centered
 * dialog on desktop. Backdrop click and Escape both close.
 */
export function BottomSheet({ open, title, onClose, children, footer }: BottomSheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={styles.sheet}
        onClick={(e) => e.stopPropagation()}
      >
        <span className={styles.handle} aria-hidden />
        <header className={styles.head}>
          <h2>{title}</h2>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={tr.common.close}
          >
            <X />
          </button>
        </header>
        <div className={styles.body}>{children}</div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body
  );
}
